/**
 * UpgradeConfirmationModal — final confirmation before flipping to Continuity.
 *
 * Two paths handled:
 *  * Beta-free user: shows "included in your beta trial, confirm to activate"
 *    and calls POST /api/subscription/upgrade — instant flip, no payment.
 *  * Paying user: shows the delta ("₦15,000 more per month") and calls the
 *    existing payment modal via the openSubscriptionModal custom event. The
 *    tier flip lands via the existing payment webhook.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (amount, symbol) => {
  if (amount == null) return '\u2014';
  return symbol + Number(amount).toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const UpgradeConfirmationModal = ({ pricing, onClose, onSuccess }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${BACKEND_URL}/api/subscription/upgrade-preview`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (r.ok) setPreview(await r.json());
        else logger.error('upgrade-preview failed', await r.text());
      } catch (err) {
        logger.error('upgrade-preview error', err);
      } finally { setLoading(false); }
    })();
  }, []);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${BACKEND_URL}/api/subscription/upgrade`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      if (data.status === 'upgraded') {
        toast.success('Welcome to Continuity System.');
        onSuccess();
        setTimeout(() => window.location.reload(), 700);
      } else if (data.status === 'checkout_required') {
        // Delegate to existing subscription/payment modal
        toast.info('Redirecting to checkout...');
        window.dispatchEvent(new CustomEvent('openSubscriptionModal', {
          detail: { defaultType: 'monthly', targetTier: 'premium_continuity' },
        }));
        onClose();
      }
    } catch (err) {
      logger.error('upgrade failed', err);
      toast.error('Could not complete upgrade. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="upgrade-confirmation-modal">
        <DialogTitle className="sr-only">Confirm upgrade to Continuity System</DialogTitle>
        <div className="flex items-start gap-3 pb-2">
          <div className="shrink-0 bg-blue-50 border border-blue-100 rounded-lg p-2">
            <Sparkles className="w-5 h-5 text-blue-700" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-serif font-semibold text-slate-900">Switch to Continuity System</h2>
            <p className="text-xs text-slate-600 mt-1">
              This unlocks the full Continuity System: Trusted Contacts, encrypted documents, and the quiet check-in cycle.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close" data-testid="upgrade-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : preview ? (
          <div className="space-y-4">
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 text-sm">
              <div className="flex justify-between text-slate-600"><span>From (Portfolio Clarity)</span><span>{fmt(preview.from.monthly_amount, preview.symbol)}/mo</span></div>
              <div className="flex justify-between text-slate-900 font-medium mt-1"><span>To (Continuity System)</span><span>{fmt(preview.to.monthly_amount, preview.symbol)}/mo</span></div>
              <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between text-slate-900 font-semibold">
                <span>Extra per month</span><span>{fmt(preview.delta_monthly, preview.symbol)}</span>
              </div>
            </div>

            {preview.beta_free ? (
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 flex items-start gap-2" data-testid="upgrade-modal-beta-note">
                <Sparkles className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-emerald-900 font-medium">Included in your beta trial.</p>
                  <p className="text-xs text-emerald-800 mt-0.5">
                    No charge today. Your beta continues free until <strong>{String(preview.beta_expires_at).slice(0, 10)}</strong>.
                  </p>
                </div>
              </div>
            ) : (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-900">
                  You&rsquo;ll be redirected to payment to complete the upgrade. The extra amount is charged pro-rata for the current cycle, and the full new price from next month.
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={onClose} data-testid="upgrade-modal-cancel">Cancel</Button>
              <Button className="flex-1 bg-blue-700 hover:bg-blue-800 text-white" onClick={handleConfirm} disabled={submitting} data-testid="upgrade-modal-confirm">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : (preview.beta_free ? 'Activate Continuity' : 'Continue to payment')}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-red-600">Could not load upgrade details.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeConfirmationModal;
